import vuexStore from "@/store"

// Matches ProgressController::PAGES_LIMIT.
const BATCH_LIMIT = 100

type Progress = Record<string, any>

type Waiter = {
  resolve: (progress: Progress) => void
  reject: (error: unknown) => void
}

const absent = (): Progress => ({ is_completed: false })

/**
 * A reactive read asks for one page's progress at a time, so a rendered list
 * asks for dozens of them within a tick. Collects the tick and reads it in one
 * request.
 */
export class ProgressBatch {
  private readonly pending = new Map<string, Waiter[]>()
  private scheduled = false

  constructor(private readonly read: (pageIds: string[]) => Promise<any>) {}

  fetch(pageId: string): Promise<Progress> {
    return new Promise((resolve, reject) => {
      const waiters = this.pending.get(pageId) ?? []
      waiters.push({ resolve, reject })
      this.pending.set(pageId, waiters)

      this.schedule()
    })
  }

  private schedule(): void {
    if (this.scheduled) return

    this.scheduled = true
    Promise.resolve().then(() => this.flush())
  }

  private flush(): Promise<void> {
    this.scheduled = false

    const batch = new Map<string, Waiter[]>()
    this.pending.forEach((waiters, pageId) => {
      if (batch.size >= BATCH_LIMIT) return
      batch.set(pageId, waiters)
    })
    batch.forEach((_, pageId) => this.pending.delete(pageId))

    if (this.pending.size) this.schedule()

    return this.read(Array.from(batch.keys()))
      .then(data => {
        const byPageId: Record<string, Progress> = data?.progress ?? {}
        const progressFor = (pageId: string) => byPageId[pageId] ?? absent()

        // One commit per batch: every commit re-renders every Code block
        // reading progress.
        vuexStore.commit(
          "pages/setPagesProgress",
          Array.from(batch.keys()).map(pageId => ({
            pageId,
            progress: progressFor(pageId)
          }))
        )

        batch.forEach((waiters, pageId) =>
          waiters.forEach(waiter => waiter.resolve(progressFor(pageId)))
        )
      })
      .catch(error =>
        batch.forEach(waiters =>
          waiters.forEach(waiter => waiter.reject(error))
        )
      )
  }
}
